import type { CancellationPolicy } from '@shared/types/reservations';
import { type AdminRole } from 'restaurantAdmin/auth/apiHelpers';
import type { RestaurantMetadata } from '../../restaurants/apiHelpers';
import { api } from '../../api';

/** Who pays Peak's processing fee on event and table purchases. */
export type ProcessingFeePayer = 'guest' | 'restaurant';

/**
 * Full restaurant from GET `/restaurants/:id` (forms, settings, checkout).
 * The authenticated summary row for the current org is {@link RestaurantMetadata}; read it with `useRestaurant()` before loading this detail.
 */
export interface AdminRestaurant extends RestaurantMetadata {
  address: string;
  cancellationPolicy: CancellationPolicy | null;
  description: string;
  dining: string | null;
  dressCode: string | null;
  dressCodeDescription: string | null;
  dressCodeRequired: boolean;
  showGoogleRating: boolean;
  processingFeePayer: ProcessingFeePayer;
  optimizeAvailabilities: boolean;
  phone: string;
  schedule: number;
  shifts: {
    id: string;
    startDate: string;
    endDate: string;
    repeat: string[];
    name: string;
    startTime: string;
    endTime: string;
  }[];
  website: string;
}

export type RestaurantAdminStatus = 'active' | 'inactive' | 'invited';

export interface Admin {
  email: string;
  fullName: string;
  role: AdminRole;
  id: string;
  status: RestaurantAdminStatus;
  reservationEmailsEnabled: boolean;
}

export const removeAdmin = async (
  restaurantId: string,
  adminId: string,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/admins/${adminId}/remove`);

export const getAdminRestaurant = async (
  restaurantId: string,
  signal?: AbortSignal,
): Promise<AdminRestaurant> =>
  api.get(`/restaurants/${restaurantId}`, { signal });

export const getAdmins = async (restaurantId: string): Promise<Admin[]> =>
  api.get(`/restaurants/${restaurantId}/admins`);

export const getAdminById = async (
  restaurantId: string,
  adminId: string,
): Promise<Admin> => api.get(`/restaurants/${restaurantId}/admins/${adminId}`);

export const updateAdminById = async (
  restaurantId: string,
  adminId: string,
  {
    role,
    reservationEmailsEnabled,
  }: { role: AdminRole; reservationEmailsEnabled: boolean },
): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/admins/${adminId}`, {
    role,
    reservationEmailsEnabled,
  });

export const inviteTeamMember = async (
  restaurantId: string,
  emails: string[],
  role: AdminRole,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/admins`, {
    emails,
    role,
  });

export const resendInvite = async (
  restaurantId: string,
  email: string,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/resend-invite`, {
    email,
  });

export const generateInvitationLink = async (
  restaurantId: string,
  email: string,
): Promise<{ invitationLink: string }> =>
  api.post(`/restaurants/${restaurantId}/invitation-link`, {
    email,
  });
